/* ============================================================
   WANDERDOLL — BASE INTERACTIONS
   Header scroll shadow, mobile menu open/close, wishlist toggle.
   Section-specific JS lives inside each section's {% javascript %} block.
============================================================ */

(function () {
  'use strict';

  // Header — scroll behaviour: transparent at top of hero pages, hide on scroll down, show on scroll up
  const header = document.querySelector('.wd-header');
  const headerSection = document.querySelector('.wd-header-section');
  if (header) {
    // Detect transparent-nav pages: homepage hero (all sizes) + PDP (mobile only).
    const hasHero = document.querySelector('.wd-hero-wrap');
    if (hasHero) document.body.classList.add('wd-has-hero');

    // PDP: transparent floating nav over the first gallery image (styled mobile-only in CSS).
    const isPdp = !!document.querySelector('.wd-pdp');
    if (isPdp) document.body.classList.add('wd-pdp-hero-nav');
    if (isPdp || hasHero) {
      // Publish the real header height so pulled-up content (PDP gallery, homepage hero)
      // tucks under the transparent nav by EXACTLY the header's height on every device.
      const setNavH = () =>
        document.documentElement.style.setProperty('--wd-nav-h', header.offsetHeight + 'px');
      setNavH();
      window.addEventListener('load', setNavH);
      // Only recompute on a REAL resize (width change / rotation). Mobile fires a `resize` on every
      // address-bar slide during scroll — recomputing then drifts the pull-up and flashes a white gap.
      let navLastW = window.innerWidth;
      window.addEventListener('resize', () => {
        if (window.innerWidth === navLastW) return;
        navLastW = window.innerWidth;
        setNavH();
      });
      window.addEventListener('orientationchange', () => setTimeout(setNavH, 100));
    }

    const transparentNav = hasHero || isPdp;

    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const goingDown = y > lastY;

      if (y > 8) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');

      // Transparent state: while scrolling DOWN from the top, KEEP the transparency until the
      // header hides — stripping it at 80px flashed a white bar over the hero for the 40px
      // until the hide kicked in. The white header belongs to scrolling UP (the reveal).
      // Guard: only preserve it if it's already present, so a mid-page re-hide (white header
      // going away after a reveal) never turns transparent over page content.
      if (transparentNav && y < 80) header.classList.add('is-at-top');
      else if (!(goingDown && header.classList.contains('is-at-top'))) header.classList.remove('is-at-top');

      if (header) {
        const past = y > 120;
        if (goingDown && past) header.classList.add('is-hidden');
        else header.classList.remove('is-hidden');
      }
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    // State is now correct for first paint — re-enable header transitions on the next frame
    // so scroll-driven changes still animate, but the initial load never does (no flash).
    requestAnimationFrame(function () { document.body.classList.remove('wd-preload'); });
  }

  // Mobile menu open/close
  const mm = document.querySelector('.wd-mm');
  const overlay = document.querySelector('.wd-mm-overlay');
  const opener = document.querySelector('.wd-hamburger');
  const closer = document.querySelector('.wd-mm-close');

  if (mm && overlay && opener && closer) {
    let lockedScrollY = 0;
    const open = () => {
      lockedScrollY = window.scrollY;
      mm.classList.add('is-open');
      overlay.classList.add('is-open');
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + lockedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    };
    const close = () => {
      mm.classList.remove('is-open');
      overlay.classList.remove('is-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, lockedScrollY);
    };
    opener.addEventListener('click', open);
    closer.addEventListener('click', close);
    overlay.addEventListener('click', close);

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mm.classList.contains('is-open')) close();
    });
  }

  // Product card mobile image swipe — black progress thumb that slides with the scroll.
  // Pixel-based translate so thumb right edge meets track right edge exactly (no subpixel gap).
  // Exposed + guarded (data-swipe-bound) so async-loaded grids (recommendations, recently-viewed)
  // can re-bind their cards — otherwise their progress pill never tracks the swipe.
  window.WD_runCardSwipes = function (root) {
    (root || document).querySelectorAll('[data-card-swipe]:not([data-swipe-bound])').forEach((track) => {
      track.setAttribute('data-swipe-bound', '');
      const trackEl = track.parentElement && track.parentElement.querySelector('[data-card-progress]');
      const thumb = trackEl && trackEl.querySelector('[data-card-progress-thumb]');
      if (!thumb || !trackEl) return;
      const items = track.querySelectorAll('.wd-card-swipe-item');
      if (items.length <= 1) return;
      let rafId = 0;
      const apply = () => {
        rafId = 0;
        const maxScroll = track.scrollWidth - track.clientWidth;
        const pct = maxScroll > 0 ? track.scrollLeft / maxScroll : 0;
        const maxTx = trackEl.clientWidth - thumb.clientWidth;
        thumb.style.transform = 'translate3d(' + (pct * maxTx) + 'px, 0, 0)';
      };
      track.addEventListener('scroll', () => {
        if (rafId) return;
        rafId = requestAnimationFrame(apply);
      }, { passive: true });
      apply();
    });
  };
  window.WD_runCardSwipes(document);

  // Wishlist heart toggle (header icon — visual only for now)
  document.querySelectorAll('.wd-wish-toggle').forEach((w) => {
    w.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      w.classList.toggle('is-active');
    });
  });

  // Product card colour count — counts sibling products that share the title prefix before " | ".
  // Uses Shopify predictive search and dedupes by base name so we never refetch.
  // Cached so lazy-loaded grids (recommendations) can reuse results.
  const wdColoursCache = new Map(); // base name → Promise<count>
  window.WD_runCardColours = function (root) {
    const scope = root || document;
    const nodes = scope.querySelectorAll('[data-card-colours]:not(.is-ready):not([data-card-colours-pending])');
    if (!nodes.length) return;

    const groups = new Map(); // base name → [nodes]
    nodes.forEach((n) => {
      const base = (n.dataset.baseName || '').trim();
      if (!base) return;
      n.setAttribute('data-card-colours-pending', '');
      if (!groups.has(base)) groups.set(base, []);
      groups.get(base).push(n);
    });

    const fillGroup = (targets, count) => {
      if (!count || count < 2) {
        targets.forEach((t) => t.removeAttribute('data-card-colours-pending'));
        return;
      }
      const label = count + (count === 1 ? ' colour' : ' colours');
      targets.forEach((t) => {
        t.textContent = label;
        t.classList.add('is-ready');
        t.removeAttribute('data-card-colours-pending');
      });
    };

    groups.forEach((targets, base) => {
      const cached = wdColoursCache.get(base);
      if (cached) {
        cached.then((count) => fillGroup(targets, count));
        return;
      }
      const url = '/search/suggest.json?q=' + encodeURIComponent(base) +
                  '&resources[type]=product' +
                  '&resources[limit]=20' +
                  '&resources[options][unavailable_products]=last' +
                  '&resources[options][fields]=title';
      const p = fetch(url)
        .then((r) => r.json())
        .then((data) => {
          const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          const prefix = (base + ' | ').toLowerCase();
          return products.filter((q) => (q.title || '').toLowerCase().indexOf(prefix) === 0).length;
        })
        .catch(() => 0);
      wdColoursCache.set(base, p);
      p.then((count) => fillGroup(targets, count));
    });
  };
  window.WD_runCardColours();
})();
